import { z } from 'zod';
import { schedulerStageCodeEnum } from './operations-scheduler';

/**
 * Operations Scheduler — Workers & attendance (phase 2a contract).
 *
 * A Worker is a shop-floor person attached to one scheduler team. Attendance is
 * a real-time ledger (clock in/out on the virtual clock), separate from the
 * engine's simulated team hours. Overtime (FULL_TIME only) is worked time beyond
 * the day's schedule, paid at 150%. Full-time workers are flagged when short of
 * 40h/week or 160h/month.
 */

export const employmentTypeEnum = z.enum(['FULL_TIME', 'PART_TIME', 'SECOND_SHIFT']);
export type EmploymentTypeValue = z.infer<typeof employmentTypeEnum>;

/** Overtime pay multiplier (150% of a normal hour). */
export const OVERTIME_MULTIPLIER = 1.5;
/** Full-time minimum contracted work: 40h/week and 160h/month (flagged when short). */
export const FULL_TIME_WEEKLY_MIN_MINUTES = 40 * 60; // 2400
export const FULL_TIME_MONTHLY_MIN_MINUTES = 160 * 60; // 9600
/** Forgotten-clock-out guard: one open session never accrues more than this. */
export const SESSION_MAX_MINUTES = 16 * 60; // 960
/** Contracted weeks in a month (160h / 40h) — turns a weekly program into a monthly one. */
export const WEEKS_PER_CONTRACTED_MONTH =
  FULL_TIME_MONTHLY_MIN_MINUTES / FULL_TIME_WEEKLY_MIN_MINUTES; // 4
/** The second shift's default program: Monday-Friday, 16:00-20:00. */
export const SECOND_SHIFT_START_MINUTES = 16 * 60;
export const SECOND_SHIFT_END_MINUTES = 20 * 60;

/**
 * Contracts agreed as a monthly salary rather than an hourly rate. Their hourly
 * rate is derived (see `hourlyRateFromMonthlySalary`) so that every downstream
 * figure - the day ledger, overtime at 150%, the punctuality bonus and the cost
 * a project carries - keeps working off a single RON/h number.
 */
export function isSalariedEmployment(employmentType: EmploymentTypeValue): boolean {
  return employmentType === 'FULL_TIME' || employmentType === 'SECOND_SHIFT';
}

/** Minutes the weekly program adds up to. */
export function weeklyScheduleMinutes(
  schedule: ReadonlyArray<{ startMinutes: number | null; endMinutes: number | null }>,
): number {
  return schedule.reduce(
    (sum, day) =>
      sum +
      (day.startMinutes !== null && day.endMinutes !== null
        ? Math.max(0, day.endMinutes - day.startMinutes)
        : 0),
    0,
  );
}

/**
 * Minutes of a stretch of work that fall outside a program window — the part
 * "beyond the program" a paid/unpaid overtime choice is about. Both are minutes
 * from 00:00 of the day the work starts, Romania's wall clock (`to` may pass
 * 1440 for work past midnight). No program that day (`null`) puts all of it
 * outside.
 */
export function minutesOutsideProgram(
  span: { from: number; to: number },
  program: { startMinutes: number; endMinutes: number } | null,
): number {
  const total = Math.max(0, span.to - span.from);
  if (!program) return total;
  const inside = Math.max(
    0,
    Math.min(span.to, program.endMinutes) - Math.max(span.from, program.startMinutes),
  );
  return total - inside;
}

/** Minutes the contract owes in a month: the weekly program x 4. */
export function contractedMonthlyMinutes(
  schedule: ReadonlyArray<{ startMinutes: number | null; endMinutes: number | null }>,
): number {
  return weeklyScheduleMinutes(schedule) * WEEKS_PER_CONTRACTED_MONTH;
}

/**
 * RON/h a monthly salary works out to, given the contracted program: full-time
 * 40h/week = 160h/month, the second shift 20h/week = 80h/month. An empty
 * program has no hours to divide by, so it yields 0 rather than infinity.
 */
export function hourlyRateFromMonthlySalary(
  monthlySalary: number,
  schedule: ReadonlyArray<{ startMinutes: number | null; endMinutes: number | null }>,
): number {
  const minutes = contractedMonthlyMinutes(schedule);
  if (minutes <= 0) return 0;
  return Math.round(((monthlySalary / minutes) * 60 + Number.EPSILON) * 100) / 100;
}

/** One weekday of the regular program. Both bounds null (or omitted) = day off. */
export const workerScheduleDaySchema = z
  .object({
    weekday: z.number().int().min(0).max(6), // 0 = Sunday … 6 = Saturday
    startMinutes: z.number().int().min(0).max(1440).nullable(),
    endMinutes: z.number().int().min(0).max(1440).nullable(),
  })
  .refine((d) => (d.startMinutes === null) === (d.endMinutes === null), {
    message: 'Both start and end must be set, or both null (day off).',
  })
  .refine(
    (d) => d.startMinutes === null || d.endMinutes === null || d.endMinutes > d.startMinutes,
    { message: 'End must be after start.' },
  );
export type WorkerScheduleDay = z.infer<typeof workerScheduleDaySchema>;

const weeklySchedule = z
  .array(workerScheduleDaySchema)
  .max(7)
  .refine((days) => new Set(days.map((d) => d.weekday)).size === days.length, {
    message: 'Duplicate weekday in schedule.',
  });

/**
 * One weekday on which the worker is available for overtime, and when: a
 * window OUTSIDE that day's program — before it, after it, or on a day off —
 * in minutes from 00:00, the same Romania wall clock as the program. It says
 * who could stay, not who did: nothing is scheduled, counted or paid from it.
 */
export const workerOvertimeDaySchema = z
  .object({
    weekday: z.number().int().min(0).max(6), // 0 = Sunday … 6 = Saturday
    startMinutes: z.number().int().min(0).max(1440),
    endMinutes: z.number().int().min(0).max(1440),
  })
  .refine((d) => d.endMinutes > d.startMinutes, { message: 'End must be after start.' });
export type WorkerOvertimeDay = z.infer<typeof workerOvertimeDaySchema>;

const weeklyOvertime = z
  .array(workerOvertimeDaySchema)
  .max(7)
  .refine((days) => new Set(days.map((d) => d.weekday)).size === days.length, {
    message: 'Duplicate weekday in overtime availability.',
  });

type ProgramDay = { weekday: number; startMinutes: number | null; endMinutes: number | null };
type Window = { startMinutes: number; endMinutes: number };

/** The program window a weekly program has on `weekday`, or null on a day off. */
function programWindowOn(schedule: ReadonlyArray<ProgramDay>, weekday: number): Window | null {
  const day = schedule.find((entry) => entry.weekday === weekday);
  if (!day || day.startMinutes === null || day.endMinutes === null) return null;
  return { startMinutes: day.startMinutes, endMinutes: day.endMinutes };
}

/**
 * The weekdays whose overtime window overlaps that day's program — an
 * availability the roster refuses, since overtime is time beyond the program.
 */
export function overtimeClashes(
  schedule: ReadonlyArray<ProgramDay>,
  overtime: ReadonlyArray<WorkerOvertimeDay>,
): number[] {
  return overtime
    .filter((window) => {
      const program = programWindowOn(schedule, window.weekday);
      return (
        program !== null &&
        window.startMinutes < program.endMinutes &&
        window.endMinutes > program.startMinutes
      );
    })
    .map((window) => window.weekday);
}

/**
 * An overtime window with the day's program taken out of it: where the two
 * overlap the program wins. What is left is the longer side — before or after
 * the program — or null when the program covers all of it.
 */
export function overtimeOutsideProgram(window: Window, program: Window | null): Window | null {
  if (!program || window.endMinutes <= program.startMinutes) return window;
  if (window.startMinutes >= program.endMinutes) return window;
  const before = { startMinutes: window.startMinutes, endMinutes: program.startMinutes };
  const after = { startMinutes: program.endMinutes, endMinutes: window.endMinutes };
  const lengthOf = (side: Window) => side.endMinutes - side.startMinutes;
  const longer = lengthOf(after) >= lengthOf(before) ? after : before;
  return lengthOf(longer) > 0 ? longer : null;
}

/**
 * Overtime availability kept outside a weekly program, day by day — what a
 * worker's availability becomes when a program is given to them (or a general
 * availability is) and the two would overlap. Days left with nothing drop out.
 */
export function clipOvertimeToSchedule(
  schedule: ReadonlyArray<ProgramDay>,
  overtime: ReadonlyArray<WorkerOvertimeDay>,
): WorkerOvertimeDay[] {
  return overtime.flatMap((day) => {
    const left = overtimeOutsideProgram(day, programWindowOn(schedule, day.weekday));
    return left ? [{ weekday: day.weekday, ...left }] : [];
  });
}

export const workerDto = z.object({
  id: z.string(),
  teamId: z.string(),
  fullName: z.string(),
  employmentType: employmentTypeEnum,
  hourlyRate: z.number(), // RON/h - derived from monthlySalary on salaried contracts
  /** The agreed monthly pay (RON) on salaried contracts; null when paid by the hour. */
  monthlySalary: z.number().nullable(),
  active: z.boolean(),
  schedule: z.array(workerScheduleDaySchema),
  /**
   * The days and hours the worker can stay for overtime, weekday order. Optional
   * only so older fixtures stay valid; the API always sends it.
   */
  overtimeAvailability: z.array(workerOvertimeDaySchema).optional(),
});
export type WorkerDto = z.infer<typeof workerDto>;

// --------------------------------------------------------------------------- //
// Annual leave ("concediu de odihnă")                                          //
// --------------------------------------------------------------------------- //

/**
 * Whole days, both ends inclusive, as UTC `YYYY-MM-DD` — the engine's day
 * (08:00–15:00 UTC) falls on the same Romanian date, so a leave day is the
 * same day wherever it is read.
 *
 * On those days the worker's crew works at the speed of the people present —
 * everywhere in the scheduler: live progress, dispatch, the projection and the
 * deadline recommendations. Nobody present, no work that day. Clock-in, the
 * timesheet and pay are not affected.
 */
const leaveCalendarDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD.');

/** The longest one leave may be, in days (a year, leap day included). */
export const WORKER_LEAVE_MAX_DAYS = 366;

const DAY_MS = 24 * 60 * 60 * 1000;

/** UTC midnight of a `YYYY-MM-DD` that names a real day (not 2026-02-30); else null. */
function leaveDayMs(date: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const ms = Date.UTC(year, month - 1, day);
  const parsed = new Date(ms);
  return parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
    ? ms
    : null;
}

/**
 * Days a leave covers, both ends counted (a one-day leave is 1). NaN when
 * either end is not a real `YYYY-MM-DD` day.
 */
export function workerLeaveDaysInclusive(leave: { startDate: string; endDate: string }): number {
  const start = leaveDayMs(leave.startDate);
  const end = leaveDayMs(leave.endDate);
  if (start === null || end === null) return Number.NaN;
  return Math.round((end - start) / DAY_MS) + 1;
}

export const workerLeaveDto = z.object({
  id: z.string(),
  startDate: leaveCalendarDate,
  /** Inclusive. */
  endDate: leaveCalendarDate,
  note: z.string().nullable(),
});
export type WorkerLeaveDto = z.infer<typeof workerLeaveDto>;

/**
 * One leave for one worker. It may not overlap another of the same worker's
 * (409 SCHEDULER_LEAVE_OVERLAP, checked by the server). A leave that has begun
 * counts from the moment it is entered: work already done before then is kept
 * at the speed the crew had.
 */
export const createWorkerLeaveRequest = z
  .object({
    startDate: leaveCalendarDate,
    /** Inclusive: the last day of leave. */
    endDate: leaveCalendarDate,
    note: z.string().trim().max(200).nullable().optional(),
  })
  // Explicit: .refine() wraps this in ZodEffects, which the global pipe does
  // not strict-upgrade.
  .strict()
  // A malformed string is already reported by the pattern; these only speak
  // about well-formed ones, and the later checks only about real days, so one
  // mistake never comes back as three errors.
  .refine(
    (v) =>
      [v.startDate, v.endDate].every(
        (date) => !leaveCalendarDate.safeParse(date).success || leaveDayMs(date) !== null,
      ),
    { message: 'Invalid date.' },
  )
  .refine((v) => !(workerLeaveDaysInclusive(v) < 1), {
    path: ['endDate'],
    message: 'The leave must end on or after its start.',
  })
  .refine((v) => !(workerLeaveDaysInclusive(v) > WORKER_LEAVE_MAX_DAYS), {
    path: ['endDate'],
    message: `At most ${WORKER_LEAVE_MAX_DAYS} days.`,
  });
export type CreateWorkerLeaveRequest = z.infer<typeof createWorkerLeaveRequest>;
export type CreateWorkerLeaveRequestInput = z.input<typeof createWorkerLeaveRequest>;

/** A worker plus live clock-in state (for the roster/attendance UI). */
export const workerRosterEntryDto = workerDto.extend({
  clockedIn: z.boolean(),
  openSessionStartedAt: z.string().datetime().nullable(),
  /** Total worked minutes to date, from settled (closed) sessions. */
  workedMinutes: z.number().int(),
  /** Total overtime minutes to date (full-time, beyond schedule). */
  overtimeMinutes: z.number().int(),
  /** Total punctuality bonus minutes earned to date (from completed stages). */
  bonusMinutes: z.number().int(),
  /**
   * Leave that has not ended yet (ending today or later, on the virtual
   * clock), earliest first. Past leave is not listed. Optional only so older
   * fixtures stay valid; the API always sends it.
   */
  leaves: z.array(workerLeaveDto).optional(),
  /** On leave on the (virtual) today. */
  onLeaveToday: z.boolean().optional(),
});
export type WorkerRosterEntryDto = z.infer<typeof workerRosterEntryDto>;

export const workerRosterTeamDto = z.object({
  teamId: z.string(),
  teamName: z.string(),
  /** Home operation — the one the roster groups this team under. */
  stageCode: z.string(),
  /** Other operations the same crew covers; shown as badges on the roster. */
  extraStageCodes: z.array(z.string()).default([]),
  /** COLLABORATOR crews are external and have no roster by design. */
  kind: z.string().default('INTERNAL'),
  clockedInCount: z.number().int(),
  /**
   * The team is running a stage right now. Mirrors the server's delete guard, so
   * the roster UI can disable "Delete team" with a reason instead of letting the
   * attempt come back as a bare 409. A team is also undeletable while it still
   * has members — that one is visible from `workers` directly.
   */
  busy: z.boolean(),
  workers: z.array(workerRosterEntryDto),
});
export type WorkerRosterTeamDto = z.infer<typeof workerRosterTeamDto>;

export const workerRosterResponse = z.object({
  /** Virtual now the roster was read at. */
  now: z.string().datetime(),
  teams: z.array(workerRosterTeamDto),
});
export type WorkerRosterResponse = z.infer<typeof workerRosterResponse>;

export const createWorkerRequest = z.object({
  fullName: z.string().trim().min(1).max(120),
  employmentType: employmentTypeEnum.default('FULL_TIME'),
  /** Ignored on salaried contracts, where the rate comes from `monthlySalary`. */
  hourlyRate: z.number().min(0).max(100_000).default(0),
  monthlySalary: z.number().min(0).max(10_000_000).nullable().optional(),
  schedule: weeklySchedule.default([]),
  /** Each window outside that day's program (refused otherwise). */
  overtimeAvailability: weeklyOvertime.default([]),
});
export type CreateWorkerRequest = z.infer<typeof createWorkerRequest>;

export const updateWorkerRequest = z
  .object({
    fullName: z.string().trim().min(1).max(120).optional(),
    employmentType: employmentTypeEnum.optional(),
    hourlyRate: z.number().min(0).max(100_000).optional(),
    /** null clears it (back to an hourly contract); omitted leaves it as it is. */
    monthlySalary: z.number().min(0).max(10_000_000).nullable().optional(),
    active: z.boolean().optional(),
    /** Reassign to another team (rejected while the worker is clocked in). */
    teamId: z.string().cuid().optional(),
    schedule: weeklySchedule.optional(),
    /** Replaces the whole availability; each window outside that day's program. */
    overtimeAvailability: weeklyOvertime.optional(),
  })
  .refine((v) => Object.values(v).some((value) => value !== undefined), {
    message: 'At least one field is required.',
  });
export type UpdateWorkerRequest = z.infer<typeof updateWorkerRequest>;

/**
 * What deleting a worker would erase. Sent as the `details` of the
 * `SCHEDULER_WORKER_HAS_HISTORY` 409 so the confirm step can name the cost
 * before the caller re-sends with `purgeHistory`.
 */
export const workerHistoryCountsDto = z.object({
  /** Clock in/out records (WorkSession). */
  sessions: z.number().int(),
  /** Settled days in the attendance ledger, where overtime lives. */
  ledgerDays: z.number().int(),
  /** Punctuality bonus entries earned on completed stages. */
  bonuses: z.number().int(),
});
export type WorkerHistoryCounts = z.infer<typeof workerHistoryCountsDto>;

// --------------------------------------------------------------------------- //
// Manual attendance — correcting the raw clock in/out ledger by hand           //
// --------------------------------------------------------------------------- //

/**
 * What a hand-entered session did to the flow (Programare operațională):
 *   CREDITED        — hours were added to the operation;
 *   ALREADY_COUNTED — the crew was on that operation in the flow for the whole
 *                     span, so the flow had counted it already;
 *   STAGE_DONE      — the operation was finished; its tally is not reopened;
 *   NO_STAGE        — the project performs none of the crew's operations, or
 *                     is not in the flow at all.
 */
export const workSessionFlowOutcomes = [
  'CREDITED',
  'ALREADY_COUNTED',
  'STAGE_DONE',
  'NO_STAGE',
] as const;
export const workSessionFlowOutcomeEnum = z.enum(workSessionFlowOutcomes);
export type WorkSessionFlowOutcome = z.infer<typeof workSessionFlowOutcomeEnum>;

/** A project named the way the scheduler names it: by its client, the title beside it. */
export const workSessionProjectRefDto = z.object({
  projectId: z.string(),
  projectCode: z.string().nullable(),
  projectTitle: z.string().nullable(),
  clientName: z.string().nullable(),
  /** The operation the crew is or was on there, when the reference comes from one. */
  stageCode: z.string().nullable(),
});
export type WorkSessionProjectRef = z.infer<typeof workSessionProjectRefDto>;

/**
 * The `details` of a SCHEDULER_SESSION_PROJECT_MISMATCH 409: who the crew is,
 * the projects it was on in the flow during the span and the ones it is on (or
 * reserved/planned for) now — both empty when it was on none — and what the
 * entry would do to the flow once confirmed.
 */
export const workSessionMismatchDetailsDto = z.object({
  teamName: z.string(),
  duringSpan: z.array(workSessionProjectRefDto),
  now: z.array(workSessionProjectRefDto),
  flowStageCode: z.string().nullable(),
  flowMinutes: z.number().int(),
  flowOutcome: workSessionFlowOutcomeEnum,
});
export type WorkSessionMismatchDetails = z.infer<typeof workSessionMismatchDetailsDto>;

/**
 * The same 409 on an entry for several people (`createWorkSessionsRequest`):
 * the alarm for each person it concerns, each measured against their own crew.
 * Confirming the batch confirms them all.
 */
export const workSessionsMismatchDetailsDto = z.object({
  workers: z.array(
    workSessionMismatchDetailsDto.extend({ workerId: z.string(), workerName: z.string() }),
  ),
});
export type WorkSessionsMismatchDetails = z.infer<typeof workSessionsMismatchDetailsDto>;

/**
 * One clock in/out record. `manual` marks the ones a manager typed or corrected;
 * the day rollup treats them exactly like clocked ones, since WorkSession is the
 * single source of truth either way (see recomputeDay).
 */
export const workSessionDto = z.object({
  id: z.string(),
  workerId: z.string(),
  startedAt: z.string().datetime(),
  /** null while the worker is still clocked in — an open session is not editable. */
  endedAt: z.string().datetime().nullable(),
  workedMinutes: z.number().int(),
  manual: z.boolean(),
  /**
   * The project these hours were spent on. A clocked session inherits it from
   * the stage its team was running when the session opened; a hand-entered one
   * carries whatever the manager picked. Null when the team had no board work
   * at the time, so the hours count as attendance but belong to no project.
   */
  projectId: z.string().nullable(),
  /** Resolved for display; null when the project no longer exists. */
  projectTitle: z.string().nullable(),
  projectCode: z.string().nullable(),
  /**
   * Which operation these hours went into, captured alongside `projectId` when
   * the session opened; a hand-entered one takes the project's operation its
   * crew performs — the one the flow link counts it on. Null for sessions
   * recorded before the operation was tracked, for attendance that belonged to
   * no board work, and for an entry whose project has none of the crew's.
   */
  stageCode: z.string().nullable(),
  /**
   * What a hand-entered entry did to the flow: the crew-minutes it added to
   * the operation (`stageCode`) and why it added nothing when it did not. Null
   * outcome for clocked sessions, which the flow counts itself, for entries
   * with no project, and for entries made before the link existed.
   */
  flowMinutes: z.number().int(),
  flowOutcome: workSessionFlowOutcomeEnum.nullable(),
  /**
   * The alarm raised when the entry was saved: its project is not one the crew
   * was on in the flow during the span, nor one it was on then. The projects
   * the crew WAS on are named; an empty list means it was on none.
   */
  projectMismatch: z.boolean(),
  assignedProjects: z.array(workSessionProjectRefDto),
  /**
   * The part of this entry beyond the day's program is unpaid: those minutes
   * count as worked (attendance, the flow) but earn nothing — neither the 150%
   * overtime premium nor the plain rate. False on clocked sessions until edited.
   * Optional only so older fixtures stay valid; the API always sends it.
   */
  unpaidOvertime: z.boolean().optional(),
});
export type WorkSessionDto = z.infer<typeof workSessionDto>;

export const workerSessionsResponse = z.object({
  workerId: z.string(),
  fullName: z.string(),
  /** Newest first. */
  sessions: z.array(workSessionDto),
});
export type WorkerSessionsResponse = z.infer<typeof workerSessionsResponse>;

/** Both bounds are required: a hand-entered session is closed by definition. */
export const createWorkSessionRequest = z.object({
  startedAt: z.string().datetime(),
  endedAt: z.string().datetime(),
  /**
   * Which project the hours were spent on. Optional — omit or send null for
   * time that belongs to no project. Unlike a clocked session, nothing can be
   * inferred here: the entry may be for a day long past, when the team was on
   * different work, so it is the manager who says.
   */
  projectId: z.string().nullable().optional(),
  /**
   * Which of the project's operations the hours went into. Omitted or null: the
   * one the person's crew performs, as the flow link picks it. Ignored without
   * a project.
   */
  stageCode: schedulerStageCodeEnum.nullable().optional(),
  /** The part beyond the day's program is unpaid — see `workSessionDto.unpaidOvertime`. */
  unpaidOvertime: z.boolean().optional(),
  /**
   * Save even though the crew was not on this project in the flow — the
   * second pass after a SCHEDULER_SESSION_PROJECT_MISMATCH refusal.
   */
  confirmProjectMismatch: z.boolean().optional(),
});
export type CreateWorkSessionRequest = z.infer<typeof createWorkSessionRequest>;

/** How many people one entry may be added for at once. */
export const WORK_SESSION_BATCH_MAX = 100;

/**
 * The same hours for several people at once — a crew that worked a shift
 * together. All or nothing: one overlap refuses the lot, and the project alarm
 * is asked once, for everyone it concerns (`workSessionsMismatchDetailsDto`).
 */
export const createWorkSessionsRequest = createWorkSessionRequest.extend({
  workerIds: z
    .array(z.string().min(1))
    .min(1)
    .max(WORK_SESSION_BATCH_MAX)
    .refine((ids) => new Set(ids).size === ids.length, { message: 'Duplicate worker.' }),
});
export type CreateWorkSessionsRequest = z.infer<typeof createWorkSessionsRequest>;

/** One entry the batch saved, named for the person it was saved for. */
export const workSessionForWorkerDto = workSessionDto.extend({ workerName: z.string() });
export type WorkSessionForWorkerDto = z.infer<typeof workSessionForWorkerDto>;

export const createWorkSessionsResponse = z.object({
  sessions: z.array(workSessionForWorkerDto),
});
export type CreateWorkSessionsResponse = z.infer<typeof createWorkSessionsResponse>;

export const updateWorkSessionRequest = z
  .object({
    startedAt: z.string().datetime().optional(),
    endedAt: z.string().datetime().optional(),
    /** null clears the attribution; omitted leaves it as it is. */
    projectId: z.string().nullable().optional(),
    /** null goes back to the crew's own operation; omitted leaves it as it is. */
    stageCode: schedulerStageCodeEnum.nullable().optional(),
    unpaidOvertime: z.boolean().optional(),
    /** As on create. An entry already confirmed for the same project is not asked again. */
    confirmProjectMismatch: z.boolean().optional(),
  })
  .refine(
    (v) =>
      v.startedAt !== undefined ||
      v.endedAt !== undefined ||
      v.projectId !== undefined ||
      v.stageCode !== undefined ||
      v.unpaidOvertime !== undefined,
    { message: 'At least one field is required.' },
  );
export type UpdateWorkSessionRequest = z.infer<typeof updateWorkSessionRequest>;

// --------------------------------------------------------------------------- //
// Statistics (phase 2c) — monthly buckets for a chosen year                   //
// --------------------------------------------------------------------------- //

export const statsYearQuery = z.object({
  year: z.coerce.number().int().min(2000).max(2100),
});
export type StatsYearQuery = z.infer<typeof statsYearQuery>;

/** RON money split for a period. */
export const workerMoneyDto = z.object({
  regular: z.number(),
  overtime: z.number(),
  bonus: z.number(),
  total: z.number(),
});

/** One calendar month of a worker's hours + money. */
export const workerMonthStatDto = z.object({
  month: z.number().int().min(1).max(12),
  workedMinutes: z.number().int(),
  overtimeMinutes: z.number().int(),
  bonusMinutes: z.number().int(),
  money: workerMoneyDto,
});
export type WorkerMonthStatDto = z.infer<typeof workerMonthStatDto>;

export const workerStatsResponse = z.object({
  workerId: z.string(),
  fullName: z.string(),
  employmentType: employmentTypeEnum,
  hourlyRate: z.number(),
  /** The agreed monthly pay (RON) on salaried contracts; null when paid by the hour. */
  monthlySalary: z.number().nullable(),
  year: z.number().int(),
  /** Years with any data (plus the requested year) — for the selector. */
  availableYears: z.array(z.number().int()),
  /** Always 12 entries, January…December. */
  months: z.array(workerMonthStatDto),
  totals: z.object({
    workedMinutes: z.number().int(),
    overtimeMinutes: z.number().int(),
    bonusMinutes: z.number().int(),
    money: z.number(),
  }),
});
export type WorkerStatsResponse = z.infer<typeof workerStatsResponse>;

/**
 * Per-project stats. Only bonus is exact per project (attendance is team-time,
 * not attributed to a project), so worked/overtime are not reported here.
 */
export const projectStatsResponse = z.object({
  projectId: z.string(),
  projectCode: z.string(),
  projectTitle: z.string(),
  year: z.number().int(),
  availableYears: z.array(z.number().int()),
  /** Bonus minutes credited on this project, per month (12 entries). */
  months: z.array(
    z.object({ month: z.number().int().min(1).max(12), bonusMinutes: z.number().int() }),
  ),
  /** Bonus breakdown by worker for the year. */
  workers: z.array(
    z.object({
      workerId: z.string(),
      fullName: z.string(),
      bonusMinutes: z.number().int(),
      money: z.number(),
    }),
  ),
  totalBonusMinutes: z.number().int(),
});
export type ProjectStatsResponse = z.infer<typeof projectStatsResponse>;

// --------------------------------------------------------------------------- //
// Timesheet — workers x days grid, over a month or any stretch of days        //
// --------------------------------------------------------------------------- //

/** The longest stretch one timesheet read covers: a quarter. */
export const TIMESHEET_MAX_DAYS = 92;

const timesheetDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD.');

/**
 * A calendar month (`year` + `month`), or any stretch of Romanian dates
 * `from`–`to` (both inclusive, at most TIMESHEET_MAX_DAYS) — a day, a week, an
 * interval. `from`/`to` win when both are given.
 */
export const timesheetQuery = z.object({
  year: z.coerce.number().int().min(2000).max(2100).optional(),
  month: z.coerce.number().int().min(1).max(12).optional(),
  from: timesheetDate.optional(),
  to: timesheetDate.optional(),
});
export type TimesheetQuery = z.infer<typeof timesheetQuery>;

/** What one day's hours were spent on, biggest share first. */
export const timesheetDayProjectDto = z.object({
  projectId: z.string(),
  projectTitle: z.string(),
  workedMinutes: z.number().int(),
});
export type TimesheetDayProjectDto = z.infer<typeof timesheetDayProjectDto>;

/** One closed session behind a day's figure — what the day view draws on its clock. */
export const timesheetSessionDto = z.object({
  id: z.string(),
  startedAt: z.string().datetime(),
  endedAt: z.string().datetime(),
  workedMinutes: z.number().int(),
  /** Named through the day's `projects`. */
  projectId: z.string().nullable(),
  stageCode: z.string().nullable(),
  manual: z.boolean(),
  unpaidOvertime: z.boolean(),
});
export type TimesheetSessionDto = z.infer<typeof timesheetSessionDto>;

/** One worked day. Days with nothing recorded are simply absent from `days`. */
export const timesheetDayDto = z.object({
  /** Day of month of `date`, 1-based. */
  day: z.number().int().min(1).max(31),
  /** The Romanian date, `YYYY-MM-DD` — what a day is matched on across months. */
  date: timesheetDate,
  workedMinutes: z.number().int(),
  /**
   * Part of workedMinutes paid as overtime (FULL_TIME beyond schedule, at
   * OVERTIME_MULTIPLIER). Excludes `unpaidMinutes`.
   */
  overtimeMinutes: z.number().int(),
  /**
   * Part of workedMinutes beyond the day's program that was entered as unpaid:
   * worked, but earning nothing. Optional only so older fixtures stay valid.
   */
  unpaidMinutes: z.number().int().optional(),
  /** The day's closed sessions, earliest first. Optional only for older fixtures. */
  sessions: z.array(timesheetSessionDto).optional(),
  /**
   * The projects this day's sessions were attributed to. Empty when none of
   * them carried a project — the hours still count, they just belong to no job.
   * Summed over the day's sessions, so it can be shorter than workedMinutes.
   */
  projects: z.array(timesheetDayProjectDto).default([]),
  /**
   * Some of the day's hours were entered on a project the crew was not on in
   * the flow — the alarm confirmed when they were saved.
   */
  projectMismatch: z.boolean().default(false),
});
export type TimesheetDayDto = z.infer<typeof timesheetDayDto>;

/**
 * Why a day carries no norm: the person's day off, a legal holiday, a day the
 * shop closes, their leave, or a day before they were employed.
 */
export const timesheetNormOffs = ['DAY_OFF', 'HOLIDAY', 'CLOSED', 'LEAVE', 'NOT_EMPLOYED'] as const;
export const timesheetNormOffEnum = z.enum(timesheetNormOffs);
export type TimesheetNormOff = z.infer<typeof timesheetNormOffEnum>;

/**
 * One day's norm for one person: the minutes they were expected to work — their
 * program on the shop's calendar, the same days and hours the flow counts them
 * on — and, when there are none, why.
 */
export const timesheetNormDayDto = z.object({
  minutes: z.number().int().nonnegative(),
  /** Null on a working day. */
  off: timesheetNormOffEnum.nullable(),
});
export type TimesheetNormDayDto = z.infer<typeof timesheetNormDayDto>;

export const timesheetWorkerDto = z.object({
  workerId: z.string(),
  fullName: z.string(),
  employmentType: employmentTypeEnum,
  active: z.boolean(),
  days: z.array(timesheetDayDto),
  /**
   * The person's norm for every date of the period, in the order of `dates`:
   * what the timesheet's colours and its weekly and monthly checks measure the
   * hours against. Optional only for older fixtures; the API always sends it.
   */
  norm: z.array(timesheetNormDayDto).optional(),
  totalWorkedMinutes: z.number().int(),
  totalOvertimeMinutes: z.number().int(),
  /** Unpaid minutes beyond the program over the period. Optional only for older fixtures. */
  totalUnpaidMinutes: z.number().int().optional(),
  /**
   * Minutes worked on days priced at 0 RON/h — recorded while the worker had
   * neither a salary nor an hourly rate, so they earn nothing until one is
   * set (which then prices them). Optional only for older fixtures.
   */
  totalUnratedMinutes: z.number().int().optional(),
  /** Punctuality bonus credited in this period, from completed stages. */
  totalBonusMinutes: z.number().int(),
  /**
   * Pay for the period, in RON. Computed with the SAME rules as the worker
   * statistics dialog — regular hours at the rate snapshotted on each day,
   * overtime at OVERTIME_MULTIPLIER, bonus minutes at the rate they were
   * credited with — so the two views can never quote different figures.
   */
  pay: workerMoneyDto,
});
export type TimesheetWorkerDto = z.infer<typeof timesheetWorkerDto>;

export const timesheetTeamDto = z.object({
  teamId: z.string(),
  teamName: z.string(),
  stageCode: z.string(),
  workers: z.array(timesheetWorkerDto),
});
export type TimesheetTeamDto = z.infer<typeof timesheetTeamDto>;

export const timesheetResponse = z.object({
  /** Period totals across every worker, so the payment section needs no summing. */
  totals: z.object({
    workedMinutes: z.number().int(),
    overtimeMinutes: z.number().int(),
    /** Optional only for older fixtures. */
    unpaidMinutes: z.number().int().optional(),
    bonusMinutes: z.number().int(),
    pay: workerMoneyDto,
  }),
  /** The year and month the period starts in. */
  year: z.number().int(),
  month: z.number().int().min(1).max(12),
  /** First and last date of the period, both inclusive. */
  from: timesheetDate,
  to: timesheetDate,
  /** Every date of the period, in order — the grid's columns. */
  dates: z.array(timesheetDate),
  /** How many dates the period has (the month's days, for a month). */
  daysInMonth: z.number().int().min(1).max(TIMESHEET_MAX_DAYS),
  /** 0 = Sunday … 6 = Saturday, for each of `dates`, to shade weekends. */
  weekdays: z.array(z.number().int().min(0).max(6)),
  teams: z.array(timesheetTeamDto),
});
export type TimesheetResponse = z.infer<typeof timesheetResponse>;

// --------------------------------------------------------------------------- //
// Project hours — what a month's labour was spent on, and what it cost         //
// --------------------------------------------------------------------------- //

/** One person's share of a project's hours for the month. */
export const projectHoursWorkerDto = z.object({
  workerId: z.string(),
  fullName: z.string(),
  /** The team the hours were clocked under, which is how they reached the project. */
  teamId: z.string(),
  teamName: z.string(),
  workedMinutes: z.number().int(),
  /** Share of the day's overtime that fell on this project (see below). */
  overtimeMinutes: z.number().int(),
  /** RON: regular minutes at the day's rate, overtime at OVERTIME_MULTIPLIER. */
  cost: z.number(),
});
export type ProjectHoursWorkerDto = z.infer<typeof projectHoursWorkerDto>;

/**
 * One operation's share of a project's hours for the month.
 *
 * The same minutes as the `workers` breakdown, cut the other way — so the two
 * lists always total the same figure, and a project can be read either as "who
 * worked on it" or as "what the work was".
 *
 * `stageCode` is null for hours clocked before the operation was recorded
 * against a session. Those minutes are deliberately left in their own bucket
 * rather than attributed to the team's current operation, which would invent
 * history: a team can be moved between operations, and a session may have run
 * under one the team no longer covers.
 */
export const projectHoursOperationDto = z.object({
  stageCode: z.string().nullable(),
  workedMinutes: z.number().int(),
  overtimeMinutes: z.number().int(),
  cost: z.number(),
});
export type ProjectHoursOperationDto = z.infer<typeof projectHoursOperationDto>;

/**
 * One project's labour for the month.
 *
 * Hours reach a project through the TEAM: a session belongs to whatever the
 * team was building at the time. Overtime, though, is a property of a whole
 * DAY (worked beyond the schedule), not of a single session — so when a worker
 * splits a day across two projects, that day's overtime is shared between them
 * in proportion to the minutes each got. Cost follows the same rules as the
 * timesheet's payment figures, from the same day-ledger rate snapshots.
 */
export const projectHoursEntryDto = z.object({
  /** null is the catch-all bucket: hours clocked with no project on the board. */
  projectId: z.string().nullable(),
  projectCode: z.string().nullable(),
  /** null when the project has since been deleted — the hours still stand. */
  projectTitle: z.string().nullable(),
  /** The warranty intervention (a WARRANTY scheduler row id) these hours went into; null = production or unattributed. */
  interventionId: z.string().nullable(),
  /** "Intervenție garanție #n"; null when interventionId is null. */
  interventionNumber: z.number().int().nullable(),
  /**
   * The board row these hours belong to — the production run, or the
   * intervention — whose per-operation figures beside the flow's
   * (`GET projects/:id/attendance`) the pane reads. Null for the unattributed
   * bucket and for a project never put in the flow. Optional only for older
   * fixtures; the API always sends it.
   */
  schedulerProjectId: z.string().nullable().optional(),
  workedMinutes: z.number().int(),
  overtimeMinutes: z.number().int(),
  cost: z.number(),
  /** Who worked on it, most hours first. */
  workers: z.array(projectHoursWorkerDto),
  /**
   * What the work was, most hours first — the same minutes as `workers`, so the
   * two lists sum to the identical total and can be shown side by side.
   */
  operations: z.array(projectHoursOperationDto),
});
export type ProjectHoursEntryDto = z.infer<typeof projectHoursEntryDto>;

export const projectHoursResponse = z.object({
  /** The year and month the period starts in. */
  year: z.number().int(),
  month: z.number().int().min(1).max(12),
  /** First and last date of the period, both inclusive. Optional only for older fixtures. */
  from: timesheetDate.optional(),
  to: timesheetDate.optional(),
  /** Most hours first; the unattributed bucket, if any, always sorts last. */
  projects: z.array(projectHoursEntryDto),
  totals: z.object({
    workedMinutes: z.number().int(),
    overtimeMinutes: z.number().int(),
    cost: z.number(),
  }),
});
export type ProjectHoursResponse = z.infer<typeof projectHoursResponse>;
